import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";
import camApi from "@/lib/camApi";

const PRIORITIES = [
  { value: "low", label: "Low" }, { value: "medium", label: "Medium" },
  { value: "high", label: "High" }, { value: "urgent", label: "Urgent" },
];

// Raises a normal ticket in the existing system, linked to the asset via cam_asset_id.
// Same categories, routing, SLA and notifications as any other ticket.
export default function RaiseTicketDialog({ asset, location, open, onClose, onRaised }) {
  const [cats, setCats] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [f, setF] = useState({
    category_id: "", sub_category_id: "", priority: "medium",
    title: asset ? `Asset issue — ${asset.name} (${asset.asset_tag})` : "",
    description: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingCats(true);
    camApi.ticketCategories()
      .then((r) => setCats(r.categories || []))
      .catch((e) => toast.error(e.message))
      .finally(() => setLoadingCats(false));
    setF((p) => ({
      ...p,
      title: asset ? `Asset issue — ${asset.name} (${asset.asset_tag})` : p.title,
      description: asset ? `Regarding asset ${asset.asset_tag} (${asset.name})${location ? ` at ${location}` : ""}.\n\n` : "",
      category_id: "", sub_category_id: "", priority: "medium",
    }));
  }, [open, asset, location]);

  const chosenCat = useMemo(() => cats.find((c) => String(c.id) === String(f.category_id)), [cats, f.category_id]);
  const subs = chosenCat?.sub_categories?.filter((s) => s.is_active !== false) || [];

  const submit = async () => {
    if (!f.category_id) { toast.error("Choose a service category."); return; }
    if (subs.length && !f.sub_category_id) { toast.error("Choose a sub-category."); return; }
    if (!f.description.trim()) { toast.error("Describe the problem."); return; }
    setBusy(true);
    try {
      const r = await camApi.raiseTicket({
        title: f.title.trim() || `Asset issue — ${asset?.asset_tag}`,
        description: f.description.trim(),
        category_id: Number(f.category_id),
        sub_category_id: f.sub_category_id ? Number(f.sub_category_id) : undefined,
        priority: f.priority,
        location: location || undefined,
        cam_asset_id: asset?.id,
      });
      toast.success(`Ticket ${r.ticket?.ticket_no || ""} raised`);
      onRaised?.(r.ticket);
      onClose?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="font-display tracking-tighter flex items-center gap-2">
            <LifeBuoy className="h-4 w-4 text-cobalt-500" />Raise a request
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {asset && (
          <div className="rounded-lg bg-black/[0.03] dark:bg-white/[0.04] px-3 py-2 text-sm">
            <span className="text-mono text-xs text-muted-foreground">{asset.asset_tag}</span> · {asset.name}
            {location ? <span className="text-muted-foreground"> · {location}</span> : null}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Service category</Label>
            <Select value={f.category_id ? String(f.category_id) : undefined} onValueChange={(v) => setF({ ...f, category_id: v, sub_category_id: "" })}>
              <SelectTrigger className="h-9 mt-1"><SelectValue placeholder={loadingCats ? "Loading…" : "Select a category"} /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {subs.length > 0 && (
            <div>
              <Label className="text-xs">Sub-category</Label>
              <Select value={f.sub_category_id ? String(f.sub_category_id) : undefined} onValueChange={(v) => setF({ ...f, sub_category_id: v })}>
                <SelectTrigger className="h-9 mt-1"><SelectValue placeholder="Select a sub-category" /></SelectTrigger>
                <SelectContent>
                  {subs.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
              <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input className="h-9 mt-1" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Describe the problem</Label>
            <Textarea className="mt-1" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What's wrong with this asset?" />
          </div>
        </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border/60 shrink-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={submit} disabled={busy}>{busy ? "Raising…" : "Raise ticket"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
